import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './EditStockModal.css';

const EditStockModal = ({ products, onSave, onClose }) => {
    const [productForms, setProductForms] = useState([]); // [{ productId, type, entries: [{ color, size, quantity, note }] }]
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const [faltaDatos, setFaltaDatos] = useState(false);

    const usedProductIds = useMemo(() => productForms.map(f => f.productId), [productForms]);

    const productOptions = useMemo(() => {
        return products.map(p => ({ value: p.uuid, label: p.name, isDisabled: usedProductIds.includes(p.uuid) }));
    }, [products, usedProductIds]);

    const handleSelectProduct = async (productId) => {
        try {
            const res = await axios.get(`https://api.minymol.com/products/${productId}`);
            const productData = res.data;

            const { variants, hasColors, hasSizes } = buildVariantsFromColorsAndSizes(productData.colors, productData.sizes);

            setProductForms(prev => [...prev, {
                productId,
                type: 'Vacío',
                variants,
                hasColors,
                hasSizes,
                entries: [{ color: '', size: '', quantity: '', note: '' }]
            }]);

            setSelectedProduct(null);
        } catch (err) {
            console.error('Error cargando producto', err);
        }
    };

    const buildVariantsFromColorsAndSizes = (colors, sizes) => {
        const variants = [];
        const hasColors = colors.length > 0;
        const hasSizes = sizes.length > 0;

        if (!hasColors && !hasSizes) {
            variants.push({ color: null, size: null, colorId: null, sizeId: null, stock: 0 });
        } else if (hasColors && !hasSizes) {
            for (let color of colors) {
                variants.push({
                    color: color.name.trim(),
                    size: null,
                    colorId: color.id,
                    sizeId: null,
                    stock: 0
                });
            }
        } else if (!hasColors && hasSizes) {
            for (let size of sizes) {
                variants.push({
                    color: null,
                    size: size.name.trim(),
                    colorId: null,
                    sizeId: size.id,
                    stock: 0
                });
            }
        } else {
            for (let color of colors) {
                for (let size of sizes) {
                    variants.push({
                        color: color.name.trim(),
                        size: size.name.trim(),
                        colorId: color.id,
                        sizeId: size.id,
                        stock: 0
                    });
                }
            }
        }

        return { variants, hasColors, hasSizes };
    };

    const getUsedCombinations = (entries) => {
        return entries.map(e => `${e.color}|${e.size}`);
    };

    const handleChange = (productIndex, entryIndex, field, value) => {
        setProductForms(prev => {
            const newForms = [...prev];
            const entry = { ...newForms[productIndex].entries[entryIndex], [field]: value };

            if (field === 'color') {
                entry.size = '';
                entry.quantity = '';
                entry.note = '';
            }
            if (field === 'size') {
                const prod = productForms[productIndex];
                const variant = prod.variants?.find(v => v.color === entry.color && v.size === value);
                entry.quantity = variant ? variant.stock : '';
                entry.note = '';
            }

            newForms[productIndex].entries[entryIndex] = entry;
            return newForms;
        });
    };

    const handleAddEntry = (productIndex) => {
        const current = productForms[productIndex].entries.slice(-1)[0];
        const form = productForms[productIndex];
        const isColorValid = !form.hasColors || current.color;
        const isSizeValid = !form.hasSizes || current.size;
        const isQuantityValid = current.quantity !== '' && Number(current.quantity) > 0;

        if (!isColorValid || !isSizeValid || !isQuantityValid) {
            setFaltaDatos(true);
            setTimeout(() => setFaltaDatos(false), 3000);
            return;
        }

        setProductForms(prev => {
            const newForms = [...prev];
            const oldEntries = newForms[productIndex].entries;
            // Clon profundo del array de entries
            const clonedEntries = oldEntries.map(e => ({ ...e }));
            clonedEntries.push({ color: '', size: '', quantity: '', note: '' });

            newForms[productIndex] = {
                ...newForms[productIndex],
                entries: clonedEntries
            };

            return newForms;
        });
    };

    const handleChangeType = (productIndex, value) => {
        setProductForms(prev => {
            const newForms = [...prev];
            newForms[productIndex].type = value;
            return newForms;
        });
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => onClose(), 300);
    };

    const handleSave = () => {
        productForms.forEach(f => {
            const entries = f.entries
                .filter(e => {
                    const isColorValid = !f.hasColors || e.color;
                    const isSizeValid = !f.hasSizes || e.size;
                    const isQuantityValid = e.quantity !== '' && Number(e.quantity) > 0;
                    return isColorValid && isSizeValid && isQuantityValid;
                })
                .map(e => {
                    const variant = f.variants.find(v =>
                        (f.hasColors ? v.color === e.color : true) &&
                        (f.hasSizes ? v.size === e.size : true)
                    );

                    const payload = {
                        type: f.type,
                        quantity: parseInt(e.quantity),
                        note: e.note || '',
                    };

                    if (variant?.colorId) payload.colorId = variant.colorId;
                    if (variant?.sizeId) payload.sizeId = variant.sizeId;

                    return payload;
                });

            entries.forEach(entry => {
                onSave(f.productId, entry); // 👈 ahora sí envías un entry individual con productId
            });
        });

        onClose();
    };

    const handleRemoveEntry = (productIndex, entryIndex) => {
        setProductForms(prev => {
            const newForms = [...prev];
            const clonedEntries = newForms[productIndex].entries.filter((_, i) => i !== entryIndex);
            newForms[productIndex] = {
                ...newForms[productIndex],
                entries: clonedEntries
            };
            return newForms;
        });
    };


    return (
        <div className="modal-overlay-container">
            <div className={`modal ${isClosing ? 'modal-slide-out' : 'modal-slide-in'}`}>
                <button className="close-btn" onClick={handleClose}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                <div className='container-add-inventory'>
                    {productForms.map((form, productIndex) => {
                        const variants = form.variants || [];
                        const usedCombinations = getUsedCombinations(form.entries);
                        const maxCombinations = new Set(variants.map(v => `${v.color}|${v.size}`)).size;

                        return (
                            <div key={form.productId} className="product-form-block">
                                <h4>{products.find(p => p.uuid === form.productId)?.name || 'Producto desconocido'}</h4>
                                <label className="label-type">Tipo de movimiento</label>
                                <Select
                                    className="big-select"
                                    classNamePrefix="react-select"
                                    value={{ value: form.type, label: form.type === 'Vacío' ? 'Seleccione un movimiento' : form.type === 'ingreso' ? 'Ingreso' : 'Egreso' }}
                                    options={[{ value: 'Vacío', label: 'Selecione un movimiento' }, { value: 'ingreso', label: 'Ingreso' }, { value: 'egreso', label: 'Egreso' }]}
                                    onChange={opt => handleChangeType(productIndex, opt.value)}
                                />

                                {form.entries.map((entry, entryIndex) => {
                                    const availableColors = Array.from(new Set(variants.map(v => v.color)));

                                    const filteredSizes = (color) => {
                                        return variants
                                            .filter(v => v.color === color)
                                            .map(v => v.size)
                                            .filter(size => !usedCombinations.includes(`${color}|${size}`) || entry.size === size);
                                    };

                                    if (form.type !== 'Vacío') {
                                        return (
                                            <div key={entryIndex} className="fields-input-variants">
                                                <div className="fields-row">
                                                    {form.hasColors && (
                                                        <div className="field">
                                                            <label>Color</label>
                                                            <Select
                                                                className="small-select"
                                                                classNamePrefix="react-select"
                                                                value={entry.color ? { value: entry.color, label: entry.color } : null}
                                                                options={availableColors.map(c => ({ value: c, label: c }))}
                                                                disabled={(form.hasColors && !entry.color) || (form.hasSizes && !entry.size)}
                                                                onChange={opt => handleChange(productIndex, entryIndex, 'color', opt.value)}
                                                            />
                                                        </div>
                                                    )}

                                                    {form.hasSizes && (
                                                        <div className="field">
                                                            <label>Talla</label>
                                                            <Select
                                                                className="small-select"
                                                                classNamePrefix="react-select"
                                                                value={entry.size ? { value: entry.size, label: entry.size } : null}
                                                                options={
                                                                    form.hasSizes
                                                                        ? (form.hasColors
                                                                            ? (entry.color ? filteredSizes(entry.color) : [])
                                                                            : Array.from(new Set(variants.map(v => v.size)))
                                                                        ).map(s => ({ value: s, label: s }))
                                                                        : []
                                                                }
                                                                placeholder={
                                                                    !form.hasSizes
                                                                        ? 'Sin tallas'
                                                                        : form.hasColors && !entry.color
                                                                            ? 'Seleccione color primero'
                                                                            : 'Talla'
                                                                }
                                                                isDisabled={!form.hasSizes || (form.hasColors && !entry.color)}
                                                                onChange={opt => handleChange(productIndex, entryIndex, 'size', opt.value)}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="field">
                                                        <label>Cantidad</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className={`num-input-inventory ${form.type}`}
                                                            value={entry?.quantity || ''}
                                                            disabled={(form.hasColors && !entry.color) || (form.hasSizes && !entry.size)}
                                                            onChange={e => handleChange(productIndex, entryIndex, 'quantity', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                {faltaDatos && (
                                                    <p className="error-message">
                                                        Por favor completa todos los campos antes de agregar otra combinación.
                                                    </p>
                                                )}
                                                <div className="field">
                                                    <label>Observaciones</label>
                                                    <textarea
                                                        className={`textarea-input-inventory ${form.type}`}
                                                        placeholder={form.type === 'ingreso' ? 'Ej: Llegada de mercadería' : 'Ej: Venta en tienda'}
                                                        value={entry.note}
                                                        onChange={e => handleChange(productIndex, entryIndex, 'note', e.target.value)}
                                                    />
                                                </div>

                                                {form.entries.length > 1 && (
                                                    <button
                                                        className="btn-remove-combination"
                                                        onClick={() => handleRemoveEntry(productIndex, entryIndex)}
                                                    >
                                                        Eliminar esta combinación
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    }
                                    else return (
                                        <></>);
                                })}

                                {(form.entries.length < maxCombinations && form.type !== 'Vacío') && (
                                    <button className="btn-add" onClick={() => handleAddEntry(productIndex)}>
                                        Agregar otra combinación
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    <Select
                        className="big-select"
                        classNamePrefix="react-select"
                        options={productOptions}
                        placeholder="Seleccionar producto"
                        value={selectedProduct}
                        onChange={opt => {
                            if (opt?.value) {
                                handleSelectProduct(opt.value);
                            }
                            setSelectedProduct(opt);
                        }}
                    />
                </div>

                <button className="btn-save" onClick={handleSave}>Guardar cambios</button>
            </div>
        </div>
    );
};

export default EditStockModal;
